import { HORNS, MAX_UPGRADE_LEVEL, PAINTS, POWER_UPS, STICKERS, type GarageKind, type OwnedItem, type Paint, type PowerUpId } from '@accra/shared';

export interface Equipped {
  paint: string;
  sticker: string;
  /** Words for a custom sticker, when sticker === 'custom'. */
  customSticker: string | null;
  horn: string;
}

export const DEFAULT_EQUIPPED: Equipped = { paint: 'accra-white', sticker: 'god-never-fails', customSticker: null, horn: 'trotro-classic' };

export interface GarageState {
  owned: readonly OwnedItem[];
  /** Special paints unlocked by play (7-day streak, 6 March). */
  unlocks: readonly string[];
  equipped: Equipped;
}

/** Highest upgrade level owned for a power-up (0 = none). */
export function upgradeLevel(state: GarageState, id: PowerUpId): number {
  let level = 0;
  for (let l = 1; l <= MAX_UPGRADE_LEVEL; l += 1) if (state.owned.some((o) => o.kind === 'upgrade' && o.id === `${id}:${l}`)) level = l;
  return level;
}

export function upgradeLevels(state: GarageState): Record<PowerUpId, number> {
  return Object.fromEntries(POWER_UPS.map((p) => [p.id, upgradeLevel(state, p.id)])) as Record<PowerUpId, number>;
}

function priceOf(kind: GarageKind, id: string): number {
  if (kind === 'upgrade') return Infinity;
  const list = kind === 'paint' ? PAINTS : kind === 'sticker' ? STICKERS : HORNS;
  return list.find((i) => i.id === id)?.price ?? Infinity;
}

export function isOwned(state: GarageState, kind: GarageKind, id: string): boolean {
  if (kind === 'paint') {
    const paint = PAINTS.find((p) => p.id === id);
    if (paint?.unlock) return state.unlocks.includes(id);
  }
  return priceOf(kind, id) === 0 || state.owned.some((o) => o.kind === kind && o.id === id);
}

export function customStickers(state: GarageState): string[] {
  return state.owned.filter((o) => o.kind === 'sticker' && o.id.startsWith('custom') && o.text).map((o) => o.text ?? '');
}

export function equippedPaint(state: GarageState): Paint {
  return PAINTS.find((p) => p.id === state.equipped.paint) ?? PAINTS[0]!;
}

export function equippedStickerText(state: GarageState): string {
  if (state.equipped.sticker === 'custom' && state.equipped.customSticker) return state.equipped.customSticker;
  return STICKERS.find((s) => s.id === state.equipped.sticker)?.text ?? STICKERS[0]!.text;
}

/** Unlocks earned by play: Streak Gold at 7 days, Ghana @ 70 on Independence Day. */
export function earnedUnlocks(streak: number, now: number): string[] {
  const unlocks: string[] = [];
  if (streak >= 7) unlocks.push('streak-gold');
  const date = new Date(now);
  if (date.getUTCMonth() === 2 && date.getUTCDate() === 6) unlocks.push('ghana-70');
  return unlocks;
}
